import { faListUl } from '@fortawesome/pro-regular-svg-icons'
import { SectionWrapper, DetailInfo, DetailRow } from '@labrav/common'
import { useStyles } from '../ViewChannelOffer.styles'
import type { ReceivedResellerAuthorization } from '../../../../modules/receivedResellerAuthorization/reducer'
import { CellWithLinkText } from '../../../../common/components/CellWithLinkText'
import { productDetailDate } from '../../../../common/utils/productCreationUtil'
import { useTimeZone } from '../../../../common/utils/hooks/useTimeZone'
import type { ChannelPartnerPrivateOffer } from '../../../../modules/channelPartnerPrivateOffer/reducer'
import { CppoPriceAdjustmentType } from '../../../../modules/channelPartnerPrivateOffer/reducer'
import { StatusChipV4, Truncate } from '@labrav/react-components'
import { snakeToTitleCase } from '@labrav/common'
import { getStatusAndLabelForResale } from '../../../../common/utils/getStatusAndLabelForResale'
import { TextWithCopy } from '../../AddEditChannelOfferComponents/AuthorizationDetails/TextWithCopy'
import { getMarkupValues } from '../../../../utils/channelOfferUtil'
import { getCurrencySymbol } from '../../../../common/utils/getCurrencySymbol'
import { getLabraResaleAuthDetailPageUrl } from '../../helpers'

export const ChannelOfferDetails = ({
  channelOffer,
  resellerAuthorization,
}: {
  channelOffer: ChannelPartnerPrivateOffer
  resellerAuthorization: ReceivedResellerAuthorization
}) => {
  const { classes } = useStyles()
  const { timeZone } = useTimeZone()

  const { modifiedDate: createdAtDate, modifiedTime: createdAtTime } =
    channelOffer?.createdAt
      ? productDetailDate(channelOffer?.createdAt, timeZone)
      : { modifiedDate: '', modifiedTime: '' }

  const { modifiedDate: endDate, modifiedTime: endDateTime } =
    channelOffer?.offerExpirationDate
      ? productDetailDate(channelOffer?.offerExpirationDate, timeZone)
      : { modifiedDate: '', modifiedTime: '' }

  const { modifiedDate: usageEndDate, modifiedTime: usageEndDateTime } =
    channelOffer?.subscriptionEndDate
      ? productDetailDate(channelOffer?.subscriptionEndDate, timeZone)
      : { modifiedDate: '', modifiedTime: '' }

  const statusDetails = getStatusAndLabelForResale(
    resellerAuthorization?.status
  )

  const { markupOrTotal } = getMarkupValues({
    dimensions: channelOffer?.dimensions,
    adjustmentPercentage: channelOffer?.adjustmentPercentage || 0,
    priceAdjustmentType: channelOffer?.priceAdjustmentType,
    resaleAuthorization: resellerAuthorization,
  })
  const code = resellerAuthorization?.currency || ''

  const markupDetailPercentageValue =
    channelOffer?.priceAdjustmentType === CppoPriceAdjustmentType.FIXED
      ? markupOrTotal
      : channelOffer?.adjustmentPercentage

  return (
    <SectionWrapper
      heading="Offer details"
      icon={faListUl}
      children={
        <div
          className={classes.section}
          data-testid="channel-offer-details-section"
        >
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={'Offer name'}
                  value={channelOffer?.offerName}
                  keyName={'offerName'}
                />
                <DetailInfo
                  title={'Authorization name'}
                  value={
                    resellerAuthorization?.agreementName ? (
                      <>
                        {
                          <CellWithLinkText
                            linkText={
                              <Truncate clipboardTooltip>
                                {resellerAuthorization?.agreementName}
                              </Truncate>
                            }
                            linkUrl={
                              getLabraResaleAuthDetailPageUrl(
                                resellerAuthorization?.id
                              ) || ''
                            }
                            linkUrlToText={false}
                          />
                        }
                      </>
                    ) : (
                      '-'
                    )
                  }
                  keyName={'resellerAuthorizationName'}
                />
              </>
            }
          />
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  gridSize={12}
                  title={'Description'}
                  value={channelOffer?.offerDescription || '-'}
                  keyName={'offerDescription'}
                />
              </>
            }
          />
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={'Buyer Name'}
                  value={channelOffer?.companyName}
                  keyName={'buyerName'}
                />
                <DetailInfo
                  title={'Buyer AWS Account ID'}
                  keyName="buyerAwsAccountId"
                  value={
                    <TextWithCopy text={channelOffer?.awsAccountId || '-'} />
                  }
                />
              </>
            }
          />
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={'Created on'}
                  value={
                    <div className={classes.dateWrapper}>
                      <div className={classes.dateValue}>{createdAtDate}</div>
                      <div className={classes.timeValue}>{createdAtTime}</div>
                    </div>
                  }
                  keyName={'offerCreatedAtDate'}
                />
                <DetailInfo
                  title={'Offer expiration date'}
                  keyName="offerExpirationDate"
                  value={
                    channelOffer?.offerExpirationDate ? (
                      <div className={classes.dateWrapper}>
                        <div className={classes.dateValue}>{endDate}</div>
                        <div className={classes.timeValue}>{endDateTime}</div>
                      </div>
                    ) : (
                      'NA'
                    )
                  }
                />
              </>
            }
          />
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={'Usage end date'}
                  value={
                    channelOffer?.subscriptionEndDate ? (
                      <div className={classes.dateWrapper}>
                        <div className={classes.dateValue}>{usageEndDate}</div>
                        <div className={classes.timeValue}>
                          {usageEndDateTime}
                        </div>
                      </div>
                    ) : (
                      'NA'
                    )
                  }
                  keyName={'usageEndDate'}
                />
                <DetailInfo
                  title={'Renewal'}
                  keyName="offerRenewal"
                  value={resellerAuthorization?.renewal ? 'Yes' : 'No'}
                />
              </>
            }
          />
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={'Currency'}
                  value={`${code}(${getCurrencySymbol(code)})`}
                  keyName={'currency'}
                />
                <DetailInfo
                  title={'Authorization status'}
                  value={<StatusChipV4 {...statusDetails} />}
                  keyName={'resellerAuthorizationStatus'}
                />
              </>
            }
          />
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={'AWS offer ID'}
                  value={<TextWithCopy text={channelOffer?.offerId || '-'} />}
                  keyName={'awsOfferId'}
                />
                <DetailInfo
                  title={'Labra offer ID'}
                  value={<TextWithCopy text={channelOffer?.id || '-'} />}
                  keyName={'labraOfferId'}
                />
              </>
            }
          />
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={'Product'}
                  value={channelOffer?.cloudProductName || '-'}
                  keyName={'productName'}
                />
                <DetailInfo
                  title={'Product type'}
                  value={snakeToTitleCase(channelOffer?.productType || '_')}
                  infoTooltip="Product type of the product for which channel offer is created."
                  keyName={'productType'}
                />
              </>
            }
          />
          <DetailRow
            children={
              <>
                <DetailInfo
                  title={'Markup percentage'}
                  value={`${markupDetailPercentageValue || '-'}%`}
                  keyName={'markupPercentage'}
                  infoTooltip={
                    'The markup percentage has been applied to calculate buyer price or total contract value.'
                  }
                />
              </>
            }
          />
        </div>
      }
    />
  )
}
